/**
 * useDashboardMetrics — derive hero-strip metrics from the filings aggregate
 * returned by `useCompanyFilingsQuery`. Pure derivation; does not hit the API.
 *
 * Why a hook: keeps Page.tsx declarative and makes the derivation unit-testable
 * in isolation later without dragging React Query into the test.
 *
 * Devil's advocate baked in:
 *   - We don't have per-filing pathway/article in the aggregate response, so
 *     "fees outstanding" is an estimate. We use the same default baseline the
 *     PaymentsModal's `calculateFilingFees` falls back to ($450 LL97 / $210
 *     LL88). Surfaced to the user as "Est." in the hero tile so we don't
 *     mislead anyone who reconciles against an actual invoice.
 *   - "Awaiting you" counts filings whose `filingContext.status.blockedBy`
 *     matches the viewer's persona (owner/rdp). Rows without filingContext
 *     are excluded rather than guessed-at (Phase 3 tightening).
 *   - "Compliance snapshot" is scoped to the currently-selected year: CY2024
 *     compliance isn't meaningful against a CY2025 filings set. Caller passes
 *     the selected year for the label.
 *   - Bucket classification (terminal / complete / exempt) reads exclusively
 *     from `filingContext.status.bucket`. The old `code >= 9001 && code <
 *     9050`-style range checks are gone; a new backend status code no
 *     longer silently mis-buckets on the FE without a deploy.
 */

import { useMemo } from "react";
import type { Filing, FilingCategory, FilingsData } from "@/api/user";
import {
  deriveOwnership,
  type Jurisdiction,
  type Ownership,
} from "@/components/common/statusTaxonomy";
import {
  getFilingContext,
  type StatusBucket as ApiStatusBucket,
  type BlockedBy,
} from "@/api/filingContext";

const EST_FEE_LL97 = 450; // mirrors calculateFilingFees default in PaymentsModal
const EST_FEE_LL88 = 210;

const DAY_MS = 24 * 60 * 60 * 1000;

export type ViewerRole = "owner" | "rdp" | "staff";

export interface DashboardMetrics {
  /** Count of filings whose deadline falls in the next 30 days. */
  deadlinesSoon: number;
  /** Nearest deadline in days, if any; used for the hero sub-label. */
  nearestDeadlineDays: number | null;
  /** Estimated $ of filing fees outstanding (unpaid). */
  feesOutstandingUsd: number;
  /** Count of unpaid filings contributing to feesOutstandingUsd. */
  unpaidFilingsCount: number;
  /** Filings waiting on the viewer (ownership matches their role). */
  awaitingYouCount: number;
  /** Total buildings represented across all filings in this snapshot. */
  totalBuildings: number;
  /** Compliant buildings (terminal complete bucket) in this snapshot. */
  compliantBuildings: number;
}

export interface TypedFiling {
  filing: Filing;
  jurisdiction: Jurisdiction;
}

/** Which ownership value is "yours" for this viewer role? */
const ownershipForRole = (role: ViewerRole): Ownership => {
  if (role === "rdp" || role === "staff") return "rdp";
  return "owner";
};

const buildingIdOf = (f: Filing): string | null => {
  if (!f.building) return null;
  if (typeof f.building === "string") return f.building;
  return f.building.id ?? null;
};

/**
 * Read a filing's deadline from `filingContext.cycle.standardDeadline`.
 *
 * Phase 3 collapsed the old "sniff four snake/camel candidates on the filing
 * row" helper into a single read from the frozen filingContext snapshot —
 * that was the field-drift risk §1 of the architecture doc called out.
 * A filing without filingContext returns `null` (no soft deadline), same as
 * before, so consumers that drop null rows from their counts are unchanged.
 */
const filingDeadline = (f: Filing): Date | null => {
  const iso = getFilingContext(f)?.cycle?.standardDeadline;
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const categoryToTyped = (cat: FilingCategory | undefined): TypedFiling[] => {
  if (!cat?.items) return [];
  const ll97 = (cat.items.lL97 ?? []).map(
    (f): TypedFiling => ({ filing: f, jurisdiction: "ll97" }),
  );
  const ll88 = (cat.items.lL88 ?? []).map(
    (f): TypedFiling => ({ filing: f, jurisdiction: "ll88" }),
  );
  return [...ll97, ...ll88];
};

const flattenFilings = (data: FilingsData | undefined): TypedFiling[] => {
  if (!data) return [];
  return [
    ...categoryToTyped(data.unstarted),
    ...categoryToTyped(data.inProgress),
    ...categoryToTyped(data.inReview),
    ...categoryToTyped(data.submitted),
    ...categoryToTyped(data.outside),
  ];
};

/**
 * Backend buckets that mean "this filing is done for this cycle" — either
 * accepted outright or carved out by exemption. Used for the "compliant
 * buildings" tile in the hero strip.
 */
const COMPLETE_BUCKETS: ReadonlySet<ApiStatusBucket> = new Set([
  "terminal_success",
  "terminal_exempt",
]);

/**
 * Backend buckets that mean "this filing has reached a final state, good or
 * bad" — no further progression possible without a manual reopen. Used for
 * fee-liability math (we don't ask an owner to pay for a terminal filing).
 */
const TERMINAL_BUCKETS: ReadonlySet<ApiStatusBucket> = new Set([
  "terminal_success",
  "terminal_exempt",
  "terminal_failed",
]);

/**
 * Dev-only: emit a one-off warning when a filing arrives without the
 * `filingContext` block we rely on. Keeps the console tidy in prod, makes
 * the gap loud during local development so a missing backend field surfaces
 * immediately instead of silently wrong-counting the metrics tile.
 */
const warnMissingFilingContext = (filing: Filing, op: string): void => {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.warn(
    `[useDashboardMetrics] ${op}: filing missing filingContext; skipping row`,
    { filingId: filing.id, status: filing.status },
  );
};

/**
 * A filing is "terminal complete" (accepted OR exempt) for compliance math.
 * Reads `filingContext.status.bucket` exclusively — Phase 3 removed the
 * `code >= 9001 && code < 9050` fallback. A row without filingContext does
 * not contribute to the "compliant buildings" count (safer than mis-bucketing
 * a brand-new 90xx code the FE doesn't know about).
 */
const isTerminalComplete = (filing: Filing): boolean => {
  const fc = getFilingContext(filing);
  if (!fc) {
    warnMissingFilingContext(filing, "isTerminalComplete");
    return false;
  }
  return COMPLETE_BUCKETS.has(fc.status.bucket);
};

/**
 * A filing generates unpaid fee liability (non-terminal AND unpaid).
 * Terminal state is read from `filingContext.status.bucket`; rows without
 * filingContext are excluded from fee math rather than silently counted
 * under the old `code >= 9001 && code < 9999` range check.
 */
const hasOpenFeeLiability = (filing: Filing, paid: boolean): boolean => {
  const fc = getFilingContext(filing);
  if (!fc) {
    warnMissingFilingContext(filing, "hasOpenFeeLiability");
    return false;
  }
  const isTerminal = TERMINAL_BUCKETS.has(fc.status.bucket);
  return !paid && !isTerminal;
};

/**
 * Resolve the `blockedBy` persona for a filing from
 * `filingContext.status.blockedBy`. Returning the backend enum
 * ("owner" / "rdp" / "carbonclear" / "dob" / null) lets the Action-needed
 * tile split owner- and CC-blocked work cleanly (Designer B §2.1).
 *
 * Phase 3 removed the Ownership → persona fallback: that mapping was lossy
 * (it had no "carbonclear" target) and stale payloads now yield `null`
 * (excluded from the "awaiting you" count) rather than being silently
 * mapped into the wrong persona.
 */
const resolveBlockedBy = (filing: Filing): BlockedBy => {
  const fc = getFilingContext(filing);
  if (fc) return fc.status.blockedBy ?? null;
  warnMissingFilingContext(filing, "resolveBlockedBy");
  return null;
};

export const useDashboardMetrics = (
  data: FilingsData | undefined,
  viewerRole: ViewerRole,
): DashboardMetrics =>
  useMemo(() => {
    const empty: DashboardMetrics = {
      deadlinesSoon: 0,
      nearestDeadlineDays: null,
      feesOutstandingUsd: 0,
      unpaidFilingsCount: 0,
      awaitingYouCount: 0,
      totalBuildings: 0,
      compliantBuildings: 0,
    };
    if (!data) return empty;

    const ownershipTarget = ownershipForRole(viewerRole);
    const all = flattenFilings(data);
    const now = Date.now();
    const thirtyDays = 30 * DAY_MS;

    // Enrich each filing with derived fields we need repeatedly.
    const enriched = all.map(({ filing, jurisdiction }) => {
      const bid = buildingIdOf(filing);
      const deadline = filingDeadline(filing);
      const deadlineDays = deadline
        ? Math.ceil((deadline.getTime() - now) / DAY_MS)
        : null;
      const blockedBy = resolveBlockedBy(filing);
      return {
        filing,
        jurisdiction,
        bid,
        deadline,
        deadlineDays,
        blockedBy,
      };
    });

    const buildingSet = new Set(
      enriched.map((e) => e.bid).filter((v): v is string => !!v),
    );
    const compliantBuildingSet = new Set(
      enriched
        .filter((e) => !!e.bid && isTerminalComplete(e.filing))
        .map((e) => e.bid as string),
    );

    const dueSoon = enriched.filter(
      (e) =>
        !!e.deadline &&
        e.deadline.getTime() - now >= 0 &&
        e.deadline.getTime() - now <= thirtyDays,
    );
    const deadlinesSoon = dueSoon.length;
    const nearestDeadlineDays = dueSoon.reduce<number | null>((min, e) => {
      if (e.deadlineDays === null) return min;
      if (min === null) return e.deadlineDays;
      return Math.min(min, e.deadlineDays);
    }, null);

    const unpaid = enriched.filter((e) =>
      hasOpenFeeLiability(e.filing, e.filing.paid),
    );
    const unpaidFilingsCount = unpaid.length;
    const feesOutstandingUsd = unpaid.reduce(
      (sum, e) =>
        sum + (e.jurisdiction === "ll97" ? EST_FEE_LL97 : EST_FEE_LL88),
      0,
    );

    // "Awaiting you" reads `filingContext.status.blockedBy` exclusively so
    // owner-blocked filings are never conflated with CC- or RDP-blocked ones
    // (Designer B §2.1). Rows without filingContext are excluded.
    const targetBlockedBy: BlockedBy =
      ownershipTarget === "owner" ? "owner" : "rdp";
    const awaitingYouCount = enriched.filter(
      (e) => e.blockedBy === targetBlockedBy,
    ).length;

    return {
      deadlinesSoon,
      nearestDeadlineDays,
      feesOutstandingUsd,
      unpaidFilingsCount,
      awaitingYouCount,
      totalBuildings: buildingSet.size,
      compliantBuildings: compliantBuildingSet.size,
    };
  }, [data, viewerRole]);

/**
 * Flatten the "action_needed" and "unstarted" categories into a single list
 * of filings the viewer is blocking on, with jurisdiction attached. Used by
 * the primary "Filings needing your action" card.
 */
export const selectActionFilings = (
  data: FilingsData | undefined,
  viewerRole: ViewerRole,
): TypedFiling[] => {
  if (!data) return [];
  const ownershipTarget = ownershipForRole(viewerRole);
  const all = flattenFilings(data);
  const filtered = all.filter(
    ({ filing, jurisdiction }) =>
      deriveOwnership(filing.status, { jurisdiction }) === ownershipTarget,
  );
  // Sort by deadline ascending (nulls last), then by status code ascending so
  // "Not Started" surfaces before "Sign Owner Attestation" — matches natural
  // onboarding order.
  return [...filtered].sort((a, b) => {
    const da = filingDeadline(a.filing)?.getTime() ?? Infinity;
    const db = filingDeadline(b.filing)?.getTime() ?? Infinity;
    if (da !== db) return da - db;
    return a.filing.status - b.filing.status;
  });
};

/**
 * Count filings in the terminal "complete" bucket.
 * Drives the "Recently completed" collapsed card.
 */
export const selectCompletedCount = (data: FilingsData | undefined): number => {
  if (!data) return 0;
  return flattenFilings(data).filter(({ filing }) => isTerminalComplete(filing))
    .length;
};

export { filingDeadline };
